/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { DeviceModel } from "../../model/device";
import { Diagnostics, Log } from "../../model/logging";
import {
  attemptSilentChunkReload,
  canAttemptRestart,
  canAttemptSilentChunkReload,
  canAttemptSilentReload,
  isChunkLoadError,
  recordRestartAttempt,
  recordSilentReloadAttempt,
  reloadApplicationAtRoot
} from "../../model/utility";

const TAG: string = "appLoadErrorRecovery";

/** HTTP status react-router puts on the error it raises for an unmatched URL. */
const STATUS_NOT_FOUND: number = 404;

/** Signature prefix for the one silent restart an unmatched URL is allowed. */
const ROUTE_NOT_FOUND_SIGNATURE_PREFIX: string = "route-not-found:";

/**
 * What the root error boundary should do with an error that reached it.
 *
 * - `silent-chunk-restart` — a failed code-split chunk on its first attempt,
 *   almost always a redeploy that rotated the chunk filenames under a running
 *   app (see `chunkLoadRecovery`).
 * - `silent-route-restart` — a URL that matches no route.
 * - `silent-restart` — anything else, while the session still has a silent
 *   restart left to spend: most render errors clear on a fresh load (a stale
 *   cache entry, a transient bad response baked into state), so the user sees
 *   nothing rather than an overlay for a failure a reload alone might fix.
 * - `overlay` — a signature-specific cooldown blocks this one (the same chunk
 *   or URL already failed a restart in the last 60s), but the session still
 *   has restart budget to spend on a *different* failure. The user gets the
 *   error overlay with Reload / Exit.
 * - `sos` — the session's restart budget is spent (`restartLimitGuard`,
 *   shared across all three silent kinds above), regardless of which kind of
 *   error triggered it. No more silent restarts are coming, so there is
 *   nothing for Reload to offer; the user gets the SOS overlay with Exit only.
 *
 * All three silent recoveries restart at `/` — see
 * {@link reloadApplicationAtRoot} for why that is the only reliable target.
 * `silent-chunk-restart`/`silent-route-restart` stay distinct from
 * `silent-restart` because they are *additionally* guarded per failure
 * signature (a chunk, a URL), so the same one doesn't loop within its own 60s
 * cooldown even while the session budget has room left.
 */
export const AppLoadErrorRecovery = {
  SilentChunkRestart: "silent-chunk-restart",
  SilentRouteRestart: "silent-route-restart",
  SilentRestart: "silent-restart",
  Overlay: "overlay",
  Sos: "sos"
} as const;

export type TAppLoadErrorRecovery =
  (typeof AppLoadErrorRecovery)[keyof typeof AppLoadErrorRecovery];

/**
 * Whether `error` is react-router's "no route matches URL" response.
 *
 * The router raises an `ErrorResponse` (status 404, `internal: true`) rather
 * than an `Error`, so it is identified by that shape. Duck-typed instead of
 * imported: react-router exports the type but not a usable runtime guard for
 * it, and the viewModel has no business importing router internals.
 */
function isRouteNotFoundError(error: unknown): boolean {
  if (error === null || error === undefined || typeof error !== "object") {
    return false;
  }
  return (error as { status?: unknown }).status === STATUS_NOT_FOUND;
}

/**
 * Signature for the unmatched URL, so a *different* bad URL later still gets
 * its own silent restart instead of inheriting this one's cooldown.
 */
function getRouteNotFoundSignature(): string {
  return ROUTE_NOT_FOUND_SIGNATURE_PREFIX + window.location.pathname;
}

/**
 * Decides the recovery for `error` without performing it. Read-only, so the
 * view can call it during render and paint nothing when a recovery is coming —
 * flashing the overlay first is exactly what "silently" rules out.
 */
export function planAppLoadErrorRecovery(
  error: unknown
): TAppLoadErrorRecovery {
  // Checked first, ahead of every per-type branch below: once the session's
  // shared restart budget is spent, nothing left to try is silent, and no
  // amount of remaining per-signature cooldown room changes that — SOS wins
  // regardless of which kind of error tripped it.
  if (!canAttemptRestart()) {
    return AppLoadErrorRecovery.Sos;
  }
  // Each recognized kind resolves fully within its own branch from here: a
  // chunk or route whose own per-signature cooldown is already spent must
  // show the (Reload-capable) overlay, not fall through to the generic
  // silent-restart below — that cooldown exists precisely because reloading
  // did not fix that specific failure within the last 60s.
  if (isChunkLoadError(error)) {
    return canAttemptSilentChunkReload(error)
      ? AppLoadErrorRecovery.SilentChunkRestart
      : AppLoadErrorRecovery.Overlay;
  }
  if (isRouteNotFoundError(error)) {
    return canAttemptSilentReload(getRouteNotFoundSignature())
      ? AppLoadErrorRecovery.SilentRouteRestart
      : AppLoadErrorRecovery.Overlay;
  }
  return AppLoadErrorRecovery.SilentRestart;
}

/**
 * Records the error that reached the root boundary. Called once per error by
 * the boundary, which cannot log for itself (the view layer must not reach into
 * `src/model`), and is the only trace left of a failure the user recovers from
 * silently.
 */
export function logAppLoadError(
  error: unknown,
  recovery: TAppLoadErrorRecovery
): void {
  Log.e(TAG, "Root boundary caught an error — recovery: " + recovery, error);
  // Log.e is console-only; without this, a crash that destroyed the render
  // tree left no trace anywhere telemetry could see it.
  Diagnostics.error(TAG, "Root boundary caught an error", error, {
    recovery,
    route: window.location.pathname,
    appVersion: DeviceModel.getAppVersionName()
  });
}

/**
 * Performs the silent recovery planned above. Returns false when the
 * navigation did not start after all — the cooldown or session budget was
 * claimed by a concurrent failure, or the attempt could not be persisted — so
 * the caller shows the overlay instead of waiting for a page load that will
 * never come.
 */
export function runSilentAppRecovery(
  error: unknown,
  recovery: TAppLoadErrorRecovery
): boolean {
  let didRecover: boolean;
  if (recovery === AppLoadErrorRecovery.SilentRouteRestart) {
    didRecover = restartForUnmatchedUrl();
  } else if (recovery === AppLoadErrorRecovery.SilentChunkRestart) {
    didRecover = attemptSilentChunkReload(error);
  } else {
    didRecover = restartForUnrecognizedError();
  }
  if (!didRecover) {
    Log.w(TAG, "Silent recovery not started — falling back to error overlay");
  }
  return didRecover;
}

/**
 * Claims one restart from the session's shared budget for an error that is
 * neither a chunk failure nor an unmatched route, then restarts at `/`. No
 * signature-specific cooldown here — unlike a chunk or a URL, a generic
 * render error has nothing stable to key a per-failure guard on, so the
 * session-wide `restartLimitGuard` is the only cap.
 */
function restartForUnrecognizedError(): boolean {
  if (!canAttemptRestart()) {
    return false;
  }
  if (!recordRestartAttempt()) {
    return false;
  }
  Log.i(TAG, "Restarting at root for an unrecognized error");
  reloadApplicationAtRoot();
  return true;
}

/** Claims the unmatched URL's one allowed restart, then restarts at `/`. */
function restartForUnmatchedUrl(): boolean {
  const signature: string = getRouteNotFoundSignature();
  if (!canAttemptSilentReload(signature)) {
    return false;
  }
  if (!canAttemptRestart()) {
    return false;
  }
  if (!recordSilentReloadAttempt(signature)) {
    return false;
  }
  if (!recordRestartAttempt()) {
    return false;
  }
  Log.i(TAG, "Restarting at root for unmatched URL: " + signature);
  reloadApplicationAtRoot();
  return true;
}

/**
 * Reload requested from the error overlay's Reload button. Restarts at `/`,
 * like every other recovery: the URL the user is on is the one that just
 * failed, so reloading it in place would land straight back on this overlay.
 */
export function reloadAppFromLoadError(): void {
  Log.i(TAG, "Reload requested from the app load error overlay");
  reloadApplicationAtRoot();
}

/**
 * Exit requested from the error overlay's Exit button. Closes the app through
 * the existing platform abstraction (TV / VegaOS only — the button is not
 * rendered in the browser).
 *
 * Lives in the viewModel so the view-layer boundary does not reach into
 * `src/model` directly (MVVM layering gate).
 */
export function exitAppFromLoadError(): void {
  Log.i(TAG, "Exit requested from the app load error overlay");
  DeviceModel.closeApplication();
}
